import type { AdminContact, EventAttendee } from "@tih/shared";
import { rowPhase, type EventRowPhase } from "./event-admin-utils";

export type CheckInEventLike = {
  id: number;
  startsAt: string;
  endsAt: string;
};

export type DirectoryPerson = {
  key: string;
  name: string;
  email: string | null;
  mobile: string | null;
  location: string | null;
  eventIds: number[];
  latestEventId: number;
  attendees: EventAttendee[];
};

export type CheckInCandidateKind = "tonight" | "contact";

export type CheckInCandidate = {
  key: string;
  kind: CheckInCandidateKind;
  name: string;
  email: string | null;
  mobile: string | null;
  location: string | null;
  attendee: EventAttendee | null;
  contact: AdminContact | null;
  person: DirectoryPerson | null;
  checkedInTonight: boolean;
  lastEventId: number | null;
};

const MIN_PHONE_DIGITS = 7;

export function normalizePersonName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export function phoneDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

export function normalizeEmail(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/** Identity: email, then phone (≥7 digits), then normalized name. */
export function personKey(input: {
  name: string;
  email?: string | null;
  mobile?: string | null;
  phone?: string | null;
}): string {
  const email = normalizeEmail(input.email);
  if (email) return `email:${email}`;
  const digits = phoneDigits(input.mobile ?? input.phone);
  if (digits.length >= MIN_PHONE_DIGITS) return `phone:${digits}`;
  return `name:${normalizePersonName(input.name)}`;
}

export function mergeAttendeeDirectory(attendees: EventAttendee[]): DirectoryPerson[] {
  const byKey = new Map<string, DirectoryPerson>();
  for (const row of attendees) {
    const key = personKey(row);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        name: row.name,
        email: row.email,
        mobile: row.mobile,
        location: row.location,
        eventIds: [row.eventId],
        latestEventId: row.eventId,
        attendees: [row],
      });
      continue;
    }
    existing.attendees.push(row);
    if (!existing.eventIds.includes(row.eventId)) existing.eventIds.push(row.eventId);
    const newer =
      Date.parse(row.createdAt) >= Date.parse(existing.attendees[0]?.createdAt ?? "") ||
      row.id > (existing.attendees.at(-2)?.id ?? 0);
    if (newer) {
      existing.name = row.name || existing.name;
      existing.email = row.email ?? existing.email;
      existing.mobile = row.mobile ?? existing.mobile;
      existing.location = row.location ?? existing.location;
      existing.latestEventId = row.eventId;
    }
  }
  return [...byKey.values()].map((person) => {
    const latest = [...person.attendees].sort((a, b) => {
      const byCreated = Date.parse(b.createdAt) - Date.parse(a.createdAt);
      if (byCreated !== 0) return byCreated;
      return b.id - a.id;
    })[0];
    return {
      ...person,
      name: latest?.name || person.name,
      email: latest?.email ?? person.email,
      mobile: latest?.mobile ?? person.mobile,
      location: latest?.location ?? person.location,
      latestEventId: latest?.eventId ?? person.latestEventId,
    };
  });
}

export function matchDirectoryQuery(
  person: {
    name: string;
    email?: string | null;
    mobile?: string | null;
    phone?: string | null;
  },
  query: string,
): number {
  const q = query.trim();
  if (!q) return 1;
  const qEmail = normalizeEmail(q);
  const email = normalizeEmail(person.email);
  if (email && q.includes("@") && (email === qEmail || email.includes(qEmail))) return 300;

  const qDigits = phoneDigits(q);
  const digits = phoneDigits(person.mobile ?? person.phone);
  if (qDigits.length >= MIN_PHONE_DIGITS && digits.length >= MIN_PHONE_DIGITS) {
    if (digits === qDigits || digits.endsWith(qDigits) || qDigits.endsWith(digits)) return 200;
    if (digits.includes(qDigits)) return 160;
  }

  const qName = normalizePersonName(q);
  const name = normalizePersonName(person.name);
  if (name === qName) return 120;
  if (name.startsWith(qName)) return 90;
  if (name.includes(qName)) return 60;
  return 0;
}

/**
 * Default door-desk event: ongoing → next upcoming → featured → most recent past.
 */
export function pickDefaultCheckInEvent(
  events: CheckInEventLike[],
  featuredId: number | null = null,
  now = Date.now(),
): number | null {
  const phaseOf = (e: CheckInEventLike): EventRowPhase => rowPhase(e, now);
  const ongoing = events.find((e) => phaseOf(e) === "ongoing");
  if (ongoing) return ongoing.id;
  const upcoming = events
    .filter((e) => phaseOf(e) === "upcoming")
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  if (upcoming) return upcoming.id;
  if (featuredId != null && events.some((e) => e.id === featuredId)) return featuredId;
  const past = events
    .filter((e) => phaseOf(e) === "past")
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt))[0];
  return past?.id ?? events[0]?.id ?? null;
}

/**
 * Desk search candidates: tonight’s EventAttendee rows, plus address-book contacts
 * for returning people (RSVP list is not a desk search source).
 */
export function buildCheckInCandidates(options: {
  attendees: EventAttendee[];
  contacts: AdminContact[];
  selectedEventId: number | null;
  query: string;
}): CheckInCandidate[] {
  const { attendees, contacts, selectedEventId, query } = options;
  const tonight = attendees.filter((a) => a.eventId === selectedEventId);
  const tonightKeys = new Set(tonight.map((a) => personKey(a)));

  const candidates: CheckInCandidate[] = [];

  for (const row of tonight) {
    const score = matchDirectoryQuery(row, query);
    if (query.trim() && score === 0) continue;
    candidates.push({
      key: `tonight:${row.id}`,
      kind: "tonight",
      name: row.name,
      email: row.email,
      mobile: row.mobile,
      location: row.location,
      attendee: row,
      contact: null,
      person: null,
      checkedInTonight: Boolean(row.checkedInAt),
      lastEventId: row.eventId,
    });
  }

  // Address book is the returning-person directory (requires a search query).
  if (query.trim()) {
    for (const contact of contacts) {
      const key = personKey({
        name: contact.name,
        email: contact.email,
        mobile: contact.phone,
      });
      if (tonightKeys.has(key)) continue;
      const score = matchDirectoryQuery(
        { name: contact.name, email: contact.email, mobile: contact.phone },
        query,
      );
      if (score === 0) continue;
      candidates.push({
        key: `contact:${contact.id}`,
        kind: "contact",
        name: contact.name,
        email: contact.email,
        mobile: contact.phone,
        location: null,
        attendee: null,
        contact,
        person: null,
        checkedInTonight: false,
        lastEventId: null,
      });
    }
  }

  return candidates.sort((a, b) => {
    const scoreDiff = matchDirectoryQuery(b, query) - matchDirectoryQuery(a, query);
    if (scoreDiff !== 0) return scoreDiff;
    const kindRank = { tonight: 0, contact: 1 } as const;
    const kindDiff = kindRank[a.kind] - kindRank[b.kind];
    if (kindDiff !== 0) return kindDiff;
    if (a.checkedInTonight !== b.checkedInTonight) return a.checkedInTonight ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

/** Top result that is not already checked in tonight — Enter on the desk. */
export function topUncheckedCandidate(candidates: CheckInCandidate[]): CheckInCandidate | null {
  return candidates.find((c) => !c.checkedInTonight) ?? null;
}

export function inferRegisterPrefill(query: string): {
  name: string;
  email: string;
  mobile: string;
} {
  const q = query.trim();
  if (!q) return { name: "", email: "", mobile: "" };
  if (q.includes("@")) return { name: "", email: q, mobile: "" };
  const digits = phoneDigits(q);
  if (digits.length >= MIN_PHONE_DIGITS && digits.length >= q.replace(/\s/g, "").length - 2) {
    return { name: "", email: "", mobile: q };
  }
  return { name: q, email: "", mobile: "" };
}
